import axios from 'axios';

// WHY A CALL FAILED, in a sentence somebody can act on.
//
// Every catch block in here used to end the same way: the server's own {error} if it sent
// one, else `e.message`, else "something went wrong". The first branch is good — the server
// says what it refused and why. The last is honest about knowing nothing. The MIDDLE one is
// the problem: axios's message for a failed request is "Request failed with status code
// 502", which is a sentence about HTTP appearing under a button about a manual, and it tells
// the one person who can act on it nothing about what to do.
//
// A bare 502 has a real meaning here and it is worth saying: nginx answered because the
// backend did not. On this deployment that is nearly always a restart in flight (a deploy
// takes about twenty seconds) or a call that outran the proxy's patience. Either way the
// answer is "try again", and that is what a message should say instead of quoting a status
// line.
//
// The status code stays in the text. It is the one thing that distinguishes a server that
// is down from one that refused, and somebody reporting the failure will be asked for it.
export function callError(e: unknown, t: (k: string) => string): string {
  if (axios.isAxiosError(e)) {
    // The server's own words first, always: it knows what it refused.
    const said = (e.response?.data as { error?: string } | undefined)?.error;
    if (said && said.trim()) return said.trim();

    const code = e.response?.status;
    // NO RESPONSE AT ALL: the request never arrived or never came back — a dropped
    // connection, a browser offline, a proxy that hung up. Distinct from a server that
    // answered with a refusal, and distinct in what to do about it.
    if (!code) return t('notice.err.noAnswer');
    // A GATEWAY FAILURE. The backend did not answer and nginx said so on its behalf, so
    // there is no JSON body to read — which is exactly why this used to surface as axios's
    // own wording.
    if (code === 502 || code === 503 || code === 504) {
      return t('notice.err.gateway').replace('{code}', String(code));
    }
    return t('notice.err.status').replace('{code}', String(code));
  }
  // Not an HTTP failure: a PDF that would not render, a parse that threw. Its own message
  // is written for a person, so it stands.
  if (e instanceof Error && e.message.trim()) return e.message.trim();
  return t('notice.err.generic');
}
